import React, { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { updateLocalPlanById, UpdateLocalPlanParams } from 'crud/configuration_api/local_plans';
import MiniLoadingSpinner from 'components/Spinners/MiniLoadingSpinner';
import './UpdateLocalPlan.scss';

interface LocalPlan {
  local_plan_id: number;
  local_id: number;
  local_name: string;
  agreed_price: number;
  month_day_billing: number;
  visits_number?: number;
  contract_start_date: string;
  trial_start_date?: string;
  trial_duration_days?: number;
  is_active: boolean;
  canceled_at?: string;
  cancellation_reason?: string;
  local_plans_metadata?: any;
}

interface UpdateLocalPlanProps {
  show: boolean;
  onHide: () => void;
  onPlanUpdated: () => void;
  plan: LocalPlan | null;
}

const UpdateLocalPlan: React.FC<UpdateLocalPlanProps> = ({ show, onHide, onPlanUpdated, plan }) => {
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [agreedPrice, setAgreedPrice] = useState<string>('');
  const [monthDayBilling, setMonthDayBilling] = useState<string>('');
  const [visitsNumber, setVisitsNumber] = useState<string>('');
  const [reason, setReason] = useState<string>('');

  useEffect(() => {
    if (show && plan) {
      // Cargar los datos del plan en el formulario
      setAgreedPrice(plan.agreed_price.toString());
      setMonthDayBilling(plan.month_day_billing.toString());
      setVisitsNumber(plan.visits_number ? plan.visits_number.toString() : '');
      setReason('');
    }
  }, [show, plan]);

  const resetForm = () => {
    setAgreedPrice('');
    setMonthDayBilling('');
    setVisitsNumber('');
    setReason('');
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const validateForm = (): boolean => {
    if (!agreedPrice || parseFloat(agreedPrice) <= 0) {
      toast.error('Debes ingresar un precio válido');
      return false;
    }
    const dayBilling = parseInt(monthDayBilling);
    if (!monthDayBilling || dayBilling < 1 || dayBilling > 31) {
      toast.error('El día de facturación debe estar entre 1 y 31');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!plan) {
      toast.error('No hay plan seleccionado');
      return;
    }

    if (!validateForm()) return;

    setLoading(true);
    try {
      const params: UpdateLocalPlanParams = {
        agreed_price: parseFloat(agreedPrice),
        month_day_billing: parseInt(monthDayBilling),
      };

      if (visitsNumber) {
        params.visits_number = parseInt(visitsNumber);
      }
      if (reason.trim()) {
        params.reason = reason.trim();
      }

      // Obtener user_id del contexto
      const userContext = localStorage.getItem('user');
      if (userContext) {
        const userData = JSON.parse(userContext);
        params.updated_by_user_id = userData.user_id;
      }

      const response = await updateLocalPlanById(plan.local_plan_id, params);
      
      if ((response as any).status === 200) {
        toast.success('Plan actualizado exitosamente');
        onPlanUpdated();
        resetForm();
      } else {
        toast.error('Error al actualizar el plan');
      }
    } catch (err: any) {
      console.error('Error updating plan:', err);
      const errorMsg = err?.response?.data?.message || 'Error al actualizar el plan';
      toast.error(errorMsg);
    }
    setLoading(false);
  };

  if (!plan) return null;

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      centered
      className="local-plan-modal"
      backdrop="static"
    >
      <Modal.Header closeButton={false}>
        <div className="form-modal-title-container">
          <h2 className="form-modal-title">Editar Plan de Local</h2>
        </div>
        <button 
          className="modal-close-button"
          onClick={handleClose}
          disabled={loading}
        >
          <X size={20} />
        </button>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <div className="mp-formgroup vertical">
            <Form.Label>Local</Form.Label>
            <Form.Control
              className="local-plan-input"
              type="text"
              value={plan.local_name}
              disabled
              readOnly
              style={{ backgroundColor: 'var(--card-color)', color: 'var(--text-color-muted)' }}
            />
          </div>

          <div className="mp-formgroup vertical">
            <Form.Label>Estado</Form.Label>
            <Form.Control
              className="local-plan-input"
              type="text"
              value={plan.is_active ? 'Activo' : 'Inactivo'}
              disabled
              readOnly
              style={{ backgroundColor: 'var(--card-color)', color: 'var(--text-color-muted)' }}
            />
          </div>

          <div className="mp-formgroup vertical">
            <Form.Label>Precio Acordado (CLP) *</Form.Label>
            <Form.Control
              className="local-plan-input"
              type="number"
              value={agreedPrice}
              onChange={(e) => setAgreedPrice(e.target.value)}
              placeholder="Ej: 50000"
              disabled={loading}
              required
              min="0"
            />
          </div>

          <div className="mp-formgroup vertical">
            <Form.Label>Día de Facturación (1-31) *</Form.Label>
            <Form.Control
              className="local-plan-input"
              type="number"
              value={monthDayBilling}
              onChange={(e) => setMonthDayBilling(e.target.value)}
              placeholder="Ej: 15"
              disabled={loading}
              required
              min="1"
              max="31"
            />
          </div>

          <div className="mp-formgroup vertical">
            <Form.Label>Número de Visitas (Opcional)</Form.Label>
            <Form.Control
              className="local-plan-input"
              type="number"
              value={visitsNumber}
              onChange={(e) => setVisitsNumber(e.target.value)}
              placeholder="Ej: 4"
              disabled={loading}
              min="0"
            />
          </div>

          <div className="mp-formgroup vertical">
            <Form.Label>Motivo del Cambio (Opcional)</Form.Label>
            <Form.Control
              className="local-plan-input"
              as="textarea"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe el motivo de la actualización..."
              disabled={loading}
            />
          </div>

          {plan.trial_start_date && (
            <div className="info-section local-plan-info">
              <p className="info-text">
                <strong>Período de Prueba:</strong> {plan.trial_start_date} 
                {plan.trial_duration_days && ` (${plan.trial_duration_days} días)`}
              </p>
            </div>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <button
            type="button"
            className="btn local-plan-btn form-button-cancel"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn local-plan-btn form-button-create"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <MiniLoadingSpinner />
                <span style={{ marginLeft: '0.5rem' }}>Actualizando...</span>
              </>
            ) : (
              'Actualizar Plan'
            )}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateLocalPlan;
